import { api_get_count_filter } from '../../../api/endpoint/count/api_get_count_filter';
import { ApiObjBaseData } from '../../../api/object/base_data/ApiObjBaseData';
import { ApiObjCountFilters } from '../../../api/object/filter/ApiObjCountFilters';
import { CountFilterBaseDataItem } from './CountFilterBaseDataItem';
import { CountFilterBaseParams } from './CountFilterBaseParams';

/**
 * Manages count calls (filter). An important reason is preventing spamming when
 * the user changes the filters quickly.
 */
export class CountFilterBaseManager {
    
    private baseData: ApiObjBaseData;
    private callbackNewData: (newData: Array<CountFilterBaseDataItem>) => void;
    private lastParams: undefined|CountFilterBaseParams;
    private countFilterData: Array<CountFilterBaseDataItem> = [];
    private requestCounter = 0;              // Used to determine if a scheduled request is the latest.
    private timestampLastParamChange = 0;    // Used to determine whether to make request immediately.

    // ================================================================
    // === Create
    // ================================================================

    constructor(baseData: ApiObjBaseData, callbackNewData: (newData: Array<CountFilterBaseDataItem>) => void) {
        this.baseData = baseData;
        this.callbackNewData = callbackNewData;
    }

    // ================================================================
    // === Actions
    // ================================================================

    public actionNewParams = (params: CountFilterBaseParams) => {
        this.lastParams = params;
        this.tryUpdateFilterCountItems();
        this.tryScheduleFilterCount();
    }

    private makeCallback = () => {
        const newData = [];
        for (const item of this.countFilterData) {
            newData.push(CountFilterBaseDataItem.createClone(item));
        }
        this.callbackNewData(newData);
    }

    // ================================================================
    // === Helpers
    // ================================================================

    private tryUpdateFilterCountItems = () : void => {

        if (this.lastParams === undefined) {
            return;
        }

        let changeFound = false;
        const newArray : Array<CountFilterBaseDataItem>= [];

        // --- Only includes valid with done count ---
        for (const countItem of this.countFilterData) {
            if (countItem.isValidForParams(this.lastParams) && countItem.state === CountFilterBaseDataItem.STATE_READY) {
                newArray.push(CountFilterBaseDataItem.createClone(countItem));
            } else {
                changeFound = true;
            }
        }

        // --- Adds new filters to check ---
        const countItemIds = [];
        for (const countItem of newArray) {
            countItemIds.push(countItem.filter_id);
        }
        for (const filterId of this.lastParams.filter_ids_to_check) {
            if (!countItemIds.includes(filterId)) {
                const filter = this.baseData.filter_map.get(filterId);
                if (filter !== undefined && filter.isSelectable()) {
                    const newItem = CountFilterBaseDataItem.createNew(filter.getId(), this.lastParams);
                    newArray.push(newItem);
                    changeFound = true;
                }
            }
        }

        if (!changeFound) {
            // Nothing was changed. Done here.
            return;
        }

        this.countFilterData = newArray;

        this.makeCallback();
    }

    private tryScheduleFilterCount = () : void => {

        let hasWaitingItems = false;
        for (const countItem of this.countFilterData) {
            if (countItem.state === CountFilterBaseDataItem.STATE_WAITING) {
                hasWaitingItems = true;
                break;
            }
        }

        if (!hasWaitingItems) {
            return; // Change did not affect counter => nothing to update.
        }

        this.requestCounter ++;
        const counterValue = this.requestCounter;

        const now = Date.now();
        const millisSinceLastChange = now - this.timestampLastParamChange;
        this.timestampLastParamChange = now;

        if (millisSinceLastChange > 5000) {
            // A long time (5 s) since last count. Run immediately.
            this.tryFetchFilterCount(counterValue);
        } else {
            // Not a long time ago. Delay count, user might update params soon.
            setTimeout(() => {this.tryFetchFilterCount(counterValue)}, 1200);
        }
    }

    private tryFetchFilterCount = (counterValue: number) => {

        if (this.lastParams === undefined) {
            return;
        }

        if (counterValue != this.requestCounter) {
            return; // Another fetch is scheduled after this. Let that run instead.
        }

        const filterIdsToCheck = [];
        for (const countItem of this.countFilterData) {
            if (countItem.state == CountFilterBaseDataItem.STATE_WAITING) {
                countItem.state = CountFilterBaseDataItem.STATE_REQUEST_SENT;
                filterIdsToCheck.push(countItem.filter_id);
            }
        }

        if (filterIdsToCheck.length === 0) {
            return;
        }

        const params = {
            filter_ids_selected: [],
            filter_ids_to_check: filterIdsToCheck,
            nix_option_id: this.lastParams.nix_option_id,
            source_id: this.lastParams.source_id,
            map_areas: [],
            use_number_source: this.lastParams.use_number_source,
        }
        api_get_count_filter(params)

            .then((result) => {
                this.handleResultFilterCount(result);

            }).catch((err) => {
                // TODO ERIK hantera fel vid count. Sätt loading items till error kanske?? Nytt state.
                console.error("Could not fetch filter count");
                throw err;
            });
    }

    private handleResultFilterCount = (result: ApiObjCountFilters) : void => {

        const countParams : CountFilterBaseParams = {
            nix_option_id: result.param_nix_option_id,
            source_id: result.param_source_id,
            filter_ids_to_check: [],
            use_number_source: result.param_use_number_source
        }

        for (const resultItem of result.result) {

            let match: CountFilterBaseDataItem|undefined = undefined;

            for (const countItem of this.countFilterData) {
                if (countItem.filter_id === resultItem.filter_id) {
                    match = countItem;
                    break;
                }
            }

            if (
                match !== undefined
                && match.state !== CountFilterBaseDataItem.STATE_READY
                && match.isValidForParams(countParams)) {
                    match.setReady(resultItem.count);
            }
        }

        this.makeCallback();
    }
}